import { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import firebase from "app/Auth";
import { AppDispatch } from "app/store";
import { actions as configActions } from "app/configSlice";
import { btnDelay, useScale } from "common/utils";
import { Select } from "common/elements/Select/Select";
import parentI18n from "features/shared/i18n";
import { updateLastSignInTime } from "features/account/api";
import { useSignUp } from "./hook";
import { BackBtn } from "./BackBtn";
import { MobileInput } from "./AuthStyles";
import { useAuthLocale } from "./useAuthLocale";

export const EmailLoginScreen = () => {
  useSignUp();
  const { currentLocale, changeLocale, localeOptions, localeLabels } = useAuthLocale();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { width, height, scale } = useScale();
  const auth = getAuth(firebase);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [localeOpen, setLocaleOpen] = useState(false);
  const langBtnRef = useRef<HTMLButtonElement>(null);

  const handleToggleLocale = () => {
    btnDelay(() => {
      setLocaleOpen(!localeOpen);
    });
  };

  const handleCloseLocale = () => {
    setLocaleOpen(false);
  };

  const handleChangeLocale = (locale: string) => {
    changeLocale(locale);
  };

  const handleLogin = async () => {
    if (!password) {
      setError(parentI18n.t("MSG_SIGN_PASSWORD_REQUIRED"));
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      updateLastSignInTime();
      dispatch(configActions.setJustAuthenticated(true));
      navigate("/");
    } catch (err) {
      console.error(err);
      setError(parentI18n.t("MSG_SIGN_LOGIN_FAILED"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex-col-el w-full h-full bg-gray2-30">
      {/* ヘッダー */}
      <div style={{ height: 90 * scale }} className="flex-col-el flex-center">
        <header
          style={{ transform: `scale(${scale})`, width: width / scale }}
          className="flex-row-el h-[90px] items-center px-6 py-4 border-b border-outline bg-white"
        >
          <BackBtn />
          <p className="ml-6 text-[27px] text-textcolor-black">
            {parentI18n.t("MSG_SIGN_IN_WITH_MAIL")}
          </p>
          {/* 言語選択ボタン - 右端に配置 */}
          <div className="ml-auto flex-row-el items-center gap-3">
            <span className="text-[20px] text-textcolor-black w-[140px] text-right">
              {parentI18n.t("MSG_PARENT_PAGE_DISPLAY_LANGUAGE")}
            </span>
            <button
              ref={langBtnRef}
              onClick={handleToggleLocale}
              className="border-0 bg-green-100 flex-row-el items-center gap-2 px-4 py-2 rounded-full cursor-pointer transition-all w-[150px]"
            >
              <span className="material-symbols-outlined text-white !text-[20px]">language</span>
              <span className="text-body text-white text-[16px] flex-1 text-center">
                {localeLabels[currentLocale] || localeLabels["ja_kanji"]}
              </span>
              <span className="material-symbols-outlined text-white !text-[16px]">expand_more</span>
            </button>
          </div>
          <Select
            anchorRef={langBtnRef}
            open={localeOpen}
            onClose={handleCloseLocale}
            value={currentLocale}
            onChange={handleChangeLocale}
            options={[...localeOptions]}
          />
        </header>
      </div>

      {/* メインコンテンツ */}
      <div
        style={{ height: height - 90 * scale }}
        className="flex-col-el items-center justify-start w-full bg-white/40"
      >
        <main
          style={{
            width: width / scale,
            height: (height - 90 * scale) / scale,
            transform: `scale(${scale})`,
          }}
          className="flex-col-el flex-center shrink-0 p-6 origin-top overflow-y-auto"
        >
          <div className="bg-white rounded-[16px] p-8 max-w-md w-full">
            <div className="flex-col-el flex-center w-full gap-4">
              <MobileInput
                type="email"
                value={email}
                onChange={(e) => {
                  setError("");
                  setEmail(e.target.value);
                }}
                placeholder={parentI18n.t("MSG_SIGN_USER_MAIL")}
                label={parentI18n.t("MSG_SIGN_USER_MAIL")}
                disabled={isLoading}
              />

              <MobileInput
                type="password"
                value={password}
                onChange={(e) => {
                  setError("");
                  setPassword(e.target.value);
                }}
                placeholder={parentI18n.t("MSG_SIGN_USER_PASSWORD")}
                label={parentI18n.t("MSG_SIGN_USER_PASSWORD")}
                disabled={isLoading}
              />

              {error && <p className="text-[14px] text-danger text-center">{error}</p>}

              <button
                className="w-[375px] py-3.5 bg-green-100 text-white rounded-[12px] text-[18px] border-none cursor-pointer hover:opacity-90 transition-all disabled:opacity-50"
                onClick={handleLogin}
                disabled={isLoading}
              >
                {parentI18n.t("MSG_SIGN_IN_BTN")}
              </button>

              {/* パスワードを忘れた場合のリンク */}
              <div className="flex-row-el flex-center mt-2">
                <span
                  className="text-blue-100 hover:text-blue-800 underline cursor-pointer text-[16px]"
                  onClick={() => {
                    btnDelay(() => {
                      navigate("/auth/password-reset");
                    });
                  }}
                >
                  {parentI18n.t("MSG_SIGN_IN_FORGOT_PASSWORD")}
                </span>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};
